import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { formatMoney } from 'utils/utils';
import { COLORS, HIDDEN_KEYS } from './CommissionsGraph';

const RADIAN = Math.PI / 180;

function isValidColor(value) {
  if (typeof value !== 'string') return false;
  const color = value.trim();
  if (!color) return false;

  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', color);
  }

  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color);
}

function renderCustomLabel({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent
}) {
  if (percent < 0.03) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill='white'
      textAnchor='middle'
      dominantBaseline='central'
      fontSize={12}
      fontWeight={600}
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
}

function CustomTooltip({ active, payload, colors }) {
  if (!active || !payload?.length) return null;
  const { name, value } = payload[0];
  return (
    <div className='bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-md text-sm'>
      <div className='flex items-center gap-2'>
        <span
          className='inline-block w-3 h-3 rounded-sm shrink-0'
          style={{ backgroundColor: colors[name] ?? '#cccccc' }}
        />
        <span className='font-semibold text-gray-700'>{name}</span>
      </div>
      <div className='text-gray-500 mt-0.5'>{formatMoney(value)}</div>
    </div>
  );
}

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  colors: PropTypes.object
};

function CommissionsPieChart({ data_for_graph, carrierColors }) {
  const resolvedColors = useMemo(() => {
    const merged = { ...COLORS };

    Object.entries(carrierColors || {}).forEach(([carrier, color]) => {
      if (isValidColor(color)) {
        merged[carrier] = color.trim();
      }
    });

    return merged;
  }, [carrierColors]);

  const pieData = useMemo(() => {
    const totals = {};
    (data_for_graph ?? []).forEach(row => {
      Object.keys(resolvedColors).forEach(carrier => {
        if (HIDDEN_KEYS.includes(carrier)) return;
        const val = Number(row?.[carrier]) || 0;
        if (val > 0) totals[carrier] = (totals[carrier] || 0) + val;
      });
    });
    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [data_for_graph, resolvedColors]);

  if (!pieData.length) {
    return (
      <div className='flex items-center justify-center h-48 text-gray-400 text-sm'>
        No data available
      </div>
    );
  }

  // Split into two columns of 4
  const col1 = pieData.slice(0, 4);
  const col2 = pieData.slice(4, 8);

  return (
    <div className='flex flex-col gap-4 pb-4'>
      <ResponsiveContainer width='100%' height={320}>
        <PieChart>
          <Pie
            data={pieData}
            cx='50%'
            cy='50%'
            outerRadius={140}
            dataKey='value'
            labelLine={false}
            label={renderCustomLabel}
          >
            {pieData.map(entry => (
              <Cell
                key={entry.name}
                fill={resolvedColors[entry.name] ?? '#cccccc'}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip colors={resolvedColors} />} />
        </PieChart>
      </ResponsiveContainer>

      {/* Custom 2-column legend */}
      <div className='grid grid-cols-2 gap-x-4 gap-y-2 px-4'>
        {[col1, col2].map((col, colIdx) => (
          <div key={colIdx} className='flex flex-col gap-2'>
            {col.map(entry => (
              <div
                key={entry.name}
                className='flex items-center justify-between gap-2'
              >
                <div className='flex items-center gap-1.5 min-w-0'>
                  <span
                    className='inline-block w-3 h-3 rounded-sm shrink-0'
                    style={{
                      backgroundColor: resolvedColors[entry.name] ?? '#cccccc'
                    }}
                  />
                  <span className='text-xs text-gray-600 truncate'>
                    {entry.name}
                  </span>
                </div>
                <span className='text-xs font-semibold text-gray-800 shrink-0'>
                  {formatMoney(entry.value)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

CommissionsPieChart.propTypes = {
  data_for_graph: PropTypes.array,
  carrierColors: PropTypes.object
};

export default CommissionsPieChart;
